import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, clearToken } from "@/lib/lamka";
import { webApi } from "@/lib/webApi";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(null);

  useEffect(() => {
    if (!user) return setIsAdmin(loading ? null : false);
    setIsAdmin(null);
    webApi.get("/me").then((r) => setIsAdmin(!!r.data.is_admin)).catch(() => setIsAdmin(false));
  }, [user, loading]);

  const loadMe = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user || data);
    } catch (e) {
      if (e.response?.status === 401) clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip while an OAuth callback is being processed
    if (window.location.hash.includes("session_id=")) return;
    loadMe();
  }, [loadMe]);

  const exchangeSession = async (sessionId) => {
    const { data } = await api.post("/auth/session", { session_id: sessionId });
    const token = data.session_token || data.token || data.access_token;
    if (!token) throw new Error("No session token returned");
    setToken(token);
    const u = data.user || data;
    setUser(u);
    setLoading(false);
    return u;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, isAdmin, exchangeSession, logout, reload: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
